/**
 * Duplicate Image Cleanup Utilities
 * เครื่องมือสำหรับทำความสะอาดรูปภาพซ้ำ
 */

import { doc, getDoc, updateDoc, arrayRemove, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ทำความสะอาดรูปซ้ำสำหรับผู้ใช้คนเดียว
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{cleanedCount: number, duplicates: Array}>}
 */
export const cleanupDuplicatesForUser = async (girlId) => {
  try {
    console.log('🧹 cleanupDuplicatesForUser started:');
    console.log('- Girl ID:', girlId);
    
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    
    if (!girlDoc.exists()) {
      console.log('❌ Girl document does not exist');
      return { cleanedCount: 0, duplicates: [] };
    }
    
    const girlData = girlDoc.data();
    const images = girlData.images || [];
    const pendingImages = girlData.pendingImages || [];
    const rejectedImages = girlData.rejectedImages || [];
    
    console.log('📊 Current Data Before Cleanup:', {
      imagesCount: images.length,
      pendingImagesCount: pendingImages.length,
      rejectedImagesCount: rejectedImages.length,
      totalImages: images.length + pendingImages.length + rejectedImages.length
    });
    
    // รวมรูปทั้งหมด
    const allImages = [...images, ...pendingImages, ...rejectedImages];
    console.log('📊 All Images:', allImages.map((img, index) => ({
      index,
      id: img.id,
      fileName: img.fileName,
      originalName: img.originalName,
      url: img.url,
      fileHash: img.fileHash,
      status: img.status
    })));
    
    // หารูปซ้ำ
    const duplicates = [];
    const seenHashes = new Set();
    const seenUrls = new Set();
    const seenNames = new Set();
    
    console.log('🔍 Checking for duplicates...');
    allImages.forEach((image, index) => {
      const hash = image.fileHash || `${image.originalName}_${image.originalSize}`;
      const url = image.url;
      const name = image.originalName;
      
      console.log(`- Checking image ${index}:`, {
        id: image.id,
        fileName: image.fileName,
        hash,
        url,
        name,
        status: image.status
      });
      
      // ตรวจสอบรูปซ้ำ
      if (seenHashes.has(hash) || seenUrls.has(url) || seenNames.has(name)) {
        const reason = seenHashes.has(hash) ? 'hash' : seenUrls.has(url) ? 'url' : 'name';
        console.log(`  ❌ DUPLICATE FOUND (${reason}):`, image.fileName);
        duplicates.push({
          ...image,
          index,
          reason
        });
      } else {
        console.log(`  ✅ Unique image:`, image.fileName);
        seenHashes.add(hash);
        seenUrls.add(url);
        seenNames.add(name);
      }
    });
    
    console.log('🔍 Duplicates Found:', duplicates.length);
    duplicates.forEach((dup, index) => {
      console.log(`  ${index + 1}. ${dup.fileName} (${dup.status}) - ${dup.reason}`);
    });
    
    // แยกรูปซ้ำตามสถานะ
    const duplicateImages = duplicates.filter(dup => dup.status === 'approved');
    const duplicatePending = duplicates.filter(dup => dup.status === 'pending');
    const duplicateRejected = duplicates.filter(dup => dup.status === 'rejected');
    
    console.log('📊 Duplicates by Status:', {
      approved: duplicateImages.length,
      pending: duplicatePending.length,
      rejected: duplicateRejected.length
    });
    
    // ลบรูปซ้ำออกจาก images
    const cleanedImages = images.filter((image, index) => 
      !duplicateImages.some(dup => dup.index === index)
    );
    
    // ลบรูปซ้ำออกจาก pendingImages
    const cleanedPendingImages = pendingImages.filter((image, index) => 
      !duplicatePending.some(dup => dup.index === index)
    );
    
    // ลบรูปซ้ำออกจาก rejectedImages
    const cleanedRejectedImages = rejectedImages.filter((image, index) => 
      !duplicateRejected.some(dup => dup.index === index)
    );
    
    console.log('📊 Cleaned Data:', {
      originalImagesCount: images.length,
      cleanedImagesCount: cleanedImages.length,
      originalPendingCount: pendingImages.length,
      cleanedPendingCount: cleanedPendingImages.length,
      originalRejectedCount: rejectedImages.length,
      cleanedRejectedCount: cleanedRejectedImages.length
    });
    
    // อัพเดทฐานข้อมูล
    console.log('💾 Updating database...');
    await updateDoc(girlRef, {
      images: cleanedImages,
      pendingImages: cleanedPendingImages,
      rejectedImages: cleanedRejectedImages,
      lastUpdated: new Date().toISOString()
    });
    
    console.log('✅ Database updated successfully');
    
    // ตรวจสอบผลลัพธ์
    console.log('🔍 Verifying cleanup...');
    const updatedGirlDoc = await getDoc(girlRef);
    const updatedGirlData = updatedGirlDoc.data();
    const updatedImages = updatedGirlData.images || [];
    const updatedPendingImages = updatedGirlData.pendingImages || [];
    const updatedRejectedImages = updatedGirlData.rejectedImages || [];
    
    console.log('📊 Final Data:', {
      imagesCount: updatedImages.length,
      pendingImagesCount: updatedPendingImages.length,
      rejectedImagesCount: updatedRejectedImages.length,
      totalImages: updatedImages.length + updatedPendingImages.length + updatedRejectedImages.length
    });
    
    console.log(`✅ Cleaned ${duplicates.length} duplicate images for user ${girlId}`);
    
    return {
      cleanedCount: duplicates.length,
      duplicates: duplicates.map(dup => ({
        fileName: dup.fileName || dup.originalName,
        status: dup.status,
        reason: dup.reason,
        url: dup.url
      }))
    };
  } catch (error) {
    console.error('💥 Error cleaning duplicates for user:', error);
    return { cleanedCount: 0, duplicates: [] };
  }
};

/**
 * ทำความสะอาดรูปซ้ำสำหรับผู้ใช้ทั้งหมด
 * @returns {Promise<{totalCleaned: number, userResults: Array}>}
 */
export const cleanupAllDuplicates = async () => {
  try {
    // ดึงข้อมูลผู้ใช้ทั้งหมด
    const usersSnapshot = await getDocs(collection(db, 'girls'));
    const results = [];
    let totalCleaned = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const result = await cleanupDuplicatesForUser(userId);
      
      if (result.cleanedCount > 0) {
        results.push({
          userId,
          cleanedCount: result.cleanedCount,
          duplicates: result.duplicates
        });
        totalCleaned += result.cleanedCount;
      }
    }
    
    console.log(`Total cleaned ${totalCleaned} duplicate images across all users`);
    
    return {
      totalCleaned,
      userResults: results
    };
  } catch (error) {
    console.error('Error cleaning all duplicates:', error);
    return { totalCleaned: 0, userResults: [] };
  }
};

/**
 * ตรวจสอบรูปซ้ำโดยไม่ลบ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{duplicateCount: number, duplicates: Array}>}
 */
export const checkDuplicatesForUser = async (girlId) => {
  try {
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    
    if (!girlDoc.exists()) {
      return { duplicateCount: 0, duplicates: [] };
    }
    
    const girlData = girlDoc.data();
    const images = girlData.images || [];
    const pendingImages = girlData.pendingImages || [];
    const rejectedImages = girlData.rejectedImages || [];
    
    // รวมรูปทั้งหมด
    const allImages = [...images, ...pendingImages, ...rejectedImages];
    
    // หารูปซ้ำ
    const duplicates = [];
    const seenHashes = new Set();
    const seenUrls = new Set();
    const seenNames = new Set();
    
    allImages.forEach((image, index) => {
      const hash = image.fileHash || `${image.originalName}_${image.originalSize}`;
      const url = image.url;
      const name = image.originalName;
      
      // ตรวจสอบรูปซ้ำ
      if (seenHashes.has(hash) || seenUrls.has(url) || seenNames.has(name)) {
        duplicates.push({
          ...image,
          index,
          reason: seenHashes.has(hash) ? 'hash' : seenUrls.has(url) ? 'url' : 'name'
        });
      } else {
        seenHashes.add(hash);
        seenUrls.add(url);
        seenNames.add(name);
      }
    });
    
    return {
      duplicateCount: duplicates.length,
      duplicates: duplicates.map(dup => ({
        fileName: dup.fileName || dup.originalName,
        status: dup.status,
        reason: dup.reason,
        url: dup.url
      }))
    };
  } catch (error) {
    console.error('Error checking duplicates for user:', error);
    return { duplicateCount: 0, duplicates: [] };
  }
};
